// IE Hub - DebtCategory Component (Refactored)
// Modern debt category manager with lender intelligence, duplicate guards, and payment offer support.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Stack,
  Chip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@/ui';
import Select from '@/ui/fields/Select';
import FreeSelect from '@/ui/fields/FreeSelect';
import SpecialTextField from '@/ui/fields/SpecialTextField';
import TextWithTooltip from '@/components/TextWithTooltip';
import SelectToggle from '@/components/SelectToggle';
import CollapsibleTextArea from '@/components/CollapsibleTextArea';
import QuestionGroupForDialog from '@/components/QuestionGroupForDialog';
import {
  AddIcon,
  EditIcon,
  DeleteIcon,
  ExpandMoreIcon,
  ExpandLessIcon,
  CloseIcon,
  InfoOutlinedIcon,
} from '@/ui';
import { useReferenceIdHelp } from '@/components/referenceId';
import { UI_MESSAGES } from '@/constants/ui';
import { useAlert } from '@/app/providers/AlertProvider';
import type {
  DebtCategoryKey,
  DebtFormEntry,
  DebtRepaymentFrequencyUi,
  EnergyCompanyRecord,
  WaterCompanyRecord,
} from '@/shared/api/types';
import type {
  CustomerDebtRecord,
  CustomerSharingResponse,
  DebtUpsertRequest,
  PaymentOfferUpsertRequest,
} from '@/shared/api/types';
import { DebtsService } from '@/shared/api/services/debtsService';
import { SharePageService } from '@/shared/api/services/sharePageService';
import { sharePageEndpoints } from '@/shared/api/endpoints';
import { httpPut } from '@/shared/api/http';
import { SharingOrchestrator } from '@/shared/api/services/sharingOrchestrator';
import { mapDebtFrequencyUiToApi } from '@/shared/lib/budgetFieldMapping';
import { getCustomerId } from '@/shared/lib/utils/getCustomerId';
import DebtMissingPrompt from '@/components/DebtMissingPrompt';
import { type DebtPromptField, isDebtAmountBlank } from '@/shared/debts/getDebtMissingPrompt';
import { useAuth } from '../shared/state/store';
import type {
  AccountTypeRecord,
  LenderParentRecord,
} from '@/shared/api/services/lenderService';
import { useLenderDisplayName } from '@/shared/api/services/lenderDisplayName';
import { buildLenderLookup as buildArrearsLenderLookup, computeSelectionContext } from '@/shared/arrears/companyRules';
import { HIDDEN_COMPANY_NAMES } from '@/pages/private/noNavButFooter/UpdateBudgetPageCom/constants';

interface DebtCategoryProps {
  label: string;
  category: DebtCategoryKey;
  value: DebtFormEntry[];
  onChange: (next: DebtFormEntry[]) => void;
  /**
   * Normalized list of creditor/company names that already exist
   * across ALL debt sections on the Debts screen (credit cards, loans, overdrafts, others, arrears).
   * Used only for a soft guard dialog when user selects a company that already exists.
   */
  existingCreditorNames?: string[];
  /**
   * Complete list of existing debts across ALL categories on the Debts screen.
   * Used for hard duplicate checking on Save (same company + same reference number).
   */
  allExistingDebts?: DebtFormEntry[];
  placeholder?: string;
  lenders?: LenderParentRecord[];
  accountTypes?: AccountTypeRecord[];
  energyCompanies?: EnergyCompanyRecord[]; // optional list from ie-energy-companies-mstr (used for electricity arrears)
  waterCompanies?: WaterCompanyRecord[]; // optional list from ie-water-companies-mstr (used for water arrears)
  readOnly?: boolean;
  loading?: boolean;
  description?: string;
  onDuplicateDetected?: (entry: DebtFormEntry) => void;
  availableBudget?: number; // optional budget for payment-offer guard
  hideAccountType?: boolean; // hide account type select for this usage
  density?: 'comfortable' | 'compact'; // compact reduces vertical spacing in dialog
  forceShowAccountType?: boolean; // force show the account type dropdown regardless of context rules
}

type DraftDebtEntry = DebtFormEntry;

const REPAYMENT_OPTIONS: { value: DebtRepaymentFrequencyUi; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: '4weekly', label: '4-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

const DEFAULT_COMPANY_OPTION = '';
const MANUAL_COMPANY_OPTION = '__manual_company__';

// Locked account type codes for specific categories (legacy parity)
const LOCKED_CATEGORY_TYPES: Partial<Record<DebtCategoryKey, { code: string; label: string }>> = {
  creditCards: { code: 'ZC', label: 'Credit card' },
  overdrafts: { code: 'OD', label: 'Overdraft' },
};

const normalize = (value: string | null | undefined): string => (value ?? '').trim().toLowerCase();


type DebtFormErrors = Partial<{
  company: string;
  manualCompany: string;
  product: string;
  creditorName: string;
  accountNumber: string;
  accTypeCode: string;
  repaymentFrequency: string;
  paymentOfferAmount: string;
}>;

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '£0.00';
  }
  return `£${Number(value).toFixed(2)}`;
};

const generateTempId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const createEmptyEntry = (category: DebtCategoryKey): DraftDebtEntry => {
  const entry: DraftDebtEntry = {
    tempId: generateTempId(),
    category,
    creditorName: '',
    parentCompanyName: null,
    lenderName: null,
    accTypeCode: null,
    accTypeId: null,
    accountTypeName: null,
    accountNumber: '',
    amountOwed: null,
    repaymentAmount: null,
    repaymentFrequency: 'monthly',
    paymentOfferAmount: null,
    paymentOfferFrequency: null,
    paymentOfferId: null,
    paymentOfferMode: null,
    shareBudget: null,
    shareNow: 'N',
    isDigital: false,
    schemaName: null,
    subDomain: null,
    isPriority: category === 'arrears',
    debtType: category === 'arrears' ? 'PRIORITY_DEBTS' : null,
    notes: null,
    arrearsCode: null,
  };

  if (category === 'arrears') {
    entry.source = 'customer_expenses';
  }

  return entry;
};

const sanitizeNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const coerceNumericId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return null;
};

// Convert a periodic repayment amount to its monthly equivalent for budgeting/balancing
const toMonthly = (
  amount: number | null | undefined,
  freq: DebtRepaymentFrequencyUi | null | undefined,
): number | null => {
  if (amount === null || amount === undefined) return null;
  if (!Number.isFinite(amount)) return null;
  switch (freq) {
    case 'weekly':
      return ((amount / 7) * 365) / 12;
    case '4weekly':
      return amount * (13 / 12);
    case 'monthly':
      return amount;
    case 'quarterly':
      return amount / 3;
    case 'yearly':
      return amount / 12;
    default:
      return null;
  }
};

const normalizeCreditor = (value: string | null | undefined): string =>
  (value ?? '').trim().toLowerCase();

// Format number for input display: round to 2 decimals and trim trailing zeros
const formatUpToTwoDecimals = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '';
  }
  const rounded = Math.round(Number(value) * 100) / 100;
  let str = rounded.toFixed(2);
  str = str.replace(/\.?0+$/, '');
  return str;
};

const hasDuplicate = (
  candidate: DraftDebtEntry,
  collection: DebtFormEntry[],
): boolean => {
  const normalizedCreditor = normalizeCreditor(candidate.creditorName);
  const normalizedAccount = normalizeCreditor(candidate.accountNumber ?? '');

  if (!normalizedCreditor || !normalizedAccount) {
    return false;
  }

  return collection.some((entry) => {
    const entryCreditor = normalizeCreditor(entry.creditorName);
    const entryAccount = normalizeCreditor(entry.accountNumber ?? '');
    const entryId = entry.id ?? entry.tempId;
    const candidateId = candidate.id ?? candidate.tempId;
    if (entryId === candidateId) {
      return false;
    }
    return entryCreditor === normalizedCreditor && entryAccount === normalizedAccount;
  });
};

// ORIGINAL category-aware helpers for filtering account types (kept commented for easy revert)
// const isLoanAccountType = (record: AccountTypeRecord): boolean => {
//   const code = (record.code || '').toUpperCase();
//   const label = (record.label || '').toLowerCase();
//   return (
//     label.includes('loan') ||
//     code === 'UL' ||
//     code === 'ML' ||
//     code === 'EL' ||
//     code === 'FL'
//   );
// };
//
// const PRIORITY_ARREARS_CODES = new Set<string>([
//   'HR', // Housing Rent Arrears
//   'MG', // Mortgage Arrears
//   'SE', // Secured Loan Arrears
//   'CT', // Council Tax Arrears
//   'GA', // Gas - Arrears
//   'EE', // Electricity - Arrears
//   'WS', // Water supply - Arrears
//   'WA', // Water waste - Arrears
// ]);
//
// const isCreditCardCode = (record: AccountTypeRecord): boolean =>
//   (record.code || '').toUpperCase() === 'ZC';
// const isOverdraftCode = (record: AccountTypeRecord): boolean =>
//   (record.code || '').toUpperCase() === 'OD';
//
// const isPriorityDebt = (record: AccountTypeRecord): boolean => {
//   const code = (record.code || '').toUpperCase();
//   const debtType = (record.debtType || '').toUpperCase();
//
//   if (PRIORITY_ARREARS_CODES.has(code)) {
//     return true;
//   }
//
//   return debtType === 'PRIORITY_DEBTS';
// };
//
// const filterAccountTypesForCategory = (
//   category: DebtCategoryKey,
//   options: AccountTypeRecord[],
// ): AccountTypeRecord[] => {
//   switch (category) {
//     case 'loans':
//       return options.filter((r) => isLoanAccountType(r) && !isPriorityDebt(r) && !isCreditCardCode(r) && !isOverdraftCode(r));
//     case 'otherDebts':
//       return options.filter((r) => !isPriorityDebt(r) && !isLoanAccountType(r) && !isCreditCardCode(r) && !isOverdraftCode(r));
//     case 'arrears':
//       return options.filter(isPriorityDebt);
//     default:
//       return options;
//   }
// };

// TEMPORARY: show all account types for all categories (no filtering).
// This is to support debugging / data review for credit cards, loans, others and arrears.
// When removing this temporary behaviour, re-introduce the ORIGINAL filterAccountTypesForCategory above.
const filterAccountTypesForCategory = (
  _category: DebtCategoryKey,
  options: AccountTypeRecord[],
): AccountTypeRecord[] => options;

const DebtCategory: React.FC<DebtCategoryProps> = ({
  label,
  category,
  value,
  onChange,
  placeholder: _placeholder, // Keep for future use
  lenders = [],
  accountTypes = [],
  energyCompanies = [],
  waterCompanies = [],
  readOnly = false,
  loading = false,
  description: _description, // Keep for future use
  onDuplicateDetected,
  existingCreditorNames = [],
  allExistingDebts,
  availableBudget,
  hideAccountType = false,
  density = 'comfortable',
  forceShowAccountType = false,
}) => {
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const lenderDisplayName = useLenderDisplayName(lenders);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const lastApostropheAlertAtRef = useRef<number>(0);
  // Track the original entry when editing so we can detect referral-breaking changes
  const editingOriginalRef = useRef<DraftDebtEntry | null>(null);
  // Track when user explicitly clears payment offer amount so we can delete the offer record.
  const paymentOfferClearedRef = useRef<boolean>(false);

  const [expanded, setExpanded] = useState<boolean>(true);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  // Field the edit view should focus once it opens (set by the "complete this debt" link)
  const [pendingFocusField, setPendingFocusField] = useState<DebtPromptField | null>(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState<boolean>(false);
  const [duplicateCompany, setDuplicateCompany] = useState<string>('');
  const [draft, setDraft] = useState<DraftDebtEntry>(() => createEmptyEntry(category));
  const [formErrors, setFormErrors] = useState<DebtFormErrors>({});
  const [companySelection, setCompanySelection] = useState<string>(DEFAULT_COMPANY_OPTION);
  const [manualCompanyName, setManualCompanyName] = useState<string>('');
  const [productSelection, setProductSelection] = useState<string>('');
  const [noReferenceId, setNoReferenceId] = useState<boolean>(false);
  const [freepostNotes, setFreepostNotes] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [deletingEntryId, setDeletingEntryId] = useState<string | number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [entryToDelete, setEntryToDelete] = useState<DebtFormEntry | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [companyDuplicateDialogOpen, setCompanyDuplicateDialogOpen] = useState<boolean>(false);
  const [pendingCompanySelection, setPendingCompanySelection] = useState<string | null>(null);
  // Keep raw text for payment-offer amount so users can type decimals naturally (e.g. ".", "0.", "0.5").
  const [paymentOfferAmountInput, setPaymentOfferAmountInput] = useState<string>('');
  const paymentOfferInputDirtyRef = useRef<boolean>(false);
  // Keep raw text for numeric inputs so users can type decimals naturally (e.g. "12.", ".5").
  // Storing only numbers causes React re-renders to drop trailing decimal points while typing.
  const [amountOwedInput, setAmountOwedInput] = useState<string>('');
  const [repaymentAmountInput, setRepaymentAmountInput] = useState<string>('');
  const amountOwedInputDirtyRef = useRef<boolean>(false);
  const repaymentAmountInputDirtyRef = useRef<boolean>(false);
  // Baseline offer amount as last known from API when the dialog opened (server-backed edits).
  // Stored in state so async refreshes (getPaymentOffer) update the baseline calculation.
  const [originalOfferAmountBaseline, setOriginalOfferAmountBaseline] = useState<number>(0);

  const debugEnabled =
    typeof process !== 'undefined' && process?.env?.NODE_ENV !== 'production';
  const debugLog = React.useCallback(
    (message: string, meta?: Record<string, unknown>) => {
      if (!debugEnabled) return;
      try {
        // eslint-disable-next-line no-console
        console.info('[DebtCategory]', message, meta || {});
      } catch {
        // never throw from logging
      }
    },
    [debugEnabled],
  );

  const getReferenceDisplay = useCallback(
    (raw: string | null | undefined): string => {
      const value = (raw ?? '').trim();
      if (!value) {
        return 'Not provided';
      }
      const email =
        typeof user?.emailAddress === 'string' ? user.emailAddress.trim() : '';
      if (email && value.toLowerCase() === email.toLowerCase()) {
        return "I don't have any Reference ID";
      }
      return value;
    },
    [user?.emailAddress],
  );

  const companyOptions = useMemo(() => {
    const hiddenSet = new Set(HIDDEN_COMPANY_NAMES.map((n) => (n ?? '').trim().toLowerCase()));

    // Base list from lenders (existing behaviour)
    // Filter out companies where addToDebtList is false (these should not appear in debt lists)
    const baseFromLenders: string[] = Array.isArray(lenders)
      ? Array.from(
        new Set(
          lenders
            .filter((record) => record.addToDebtList === true)
            .map((record) => record.parentCompany)
            .filter((name): name is string => Boolean(name && name.trim().length > 0)),
        ),
      )
      : [];

    const combinedSet = new Set<string>();
    baseFromLenders.forEach((name) => {
      const key = name.trim().toLowerCase();
      if (key && !hiddenSet.has(key)) {
        combinedSet.add(name);
      }
    });

    // For arrears dialog, also include company list from ie-energy-companies-mstr
    if (category === 'arrears' && Array.isArray(energyCompanies)) {
      energyCompanies.forEach((company) => {
        const name = (company?.companyName ?? '').trim();
        const key = name.toLowerCase();
        if (name && !hiddenSet.has(key)) {
          combinedSet.add(name);
        }
      });
    }

    // For arrears dialog, also include company list from ie-water-companies-mstr
    if (category === 'arrears' && Array.isArray(waterCompanies)) {
      waterCompanies.forEach((company) => {
        const name = (company?.companyName ?? '').trim();
        const key = name.toLowerCase();
        if (name && !hiddenSet.has(key)) {
          combinedSet.add(name);
        }
      });
    }

    return Array.from(combinedSet).sort((a, b) => a.localeCompare(b));
  }, [lenders, energyCompanies, waterCompanies, category]);

  const allAccountTypeOptions = useMemo(() => {
    if (!Array.isArray(accountTypes)) {
      return [] as Array<{ value: string; label: string; record: AccountTypeRecord }>;
    }
    return accountTypes.map((option) => ({
      value: option.code,
      label: option.label,
      record: option,
    }));
  }, [accountTypes]);

  // Normalized set of existing creditor/company names across ALL debt sections.
  // Used to trigger a soft confirmation when user selects a company that already exists.
  const existingCreditorNameSet = useMemo(() => {
    const set = new Set<string>();
    if (Array.isArray(existingCreditorNames)) {
      existingCreditorNames.forEach((name) => {
        const key = normalize(name);
        if (key) {
          set.add(key);
        }
      });
    }
    return set;
  }, [existingCreditorNames]);

  const lenderLookup = useMemo(() => buildArrearsLenderLookup(lenders), [lenders]);

  const selectionContext = useMemo(
    () =>
      computeSelectionContext({
        companySelection,
        productSelection,
        manualCompanyName,
        lenders,
        accountTypes,
        lookup: lenderLookup,
        manual: companySelection === MANUAL_COMPANY_OPTION,
      }),
    [companySelection, productSelection, manualCompanyName, lenders, accountTypes, lenderLookup]
  );

  // Category-aware filtered options for non-arrears categories
  const filteredAccountTypeOptions = useMemo(() => {
    if (category === 'arrears') return [] as Array<{ value: string; label: string; record: AccountTypeRecord }>;
    // Fallback to all accountTypes when no company is selected or context has no options
    const base = (selectionContext.accountTypeOptions && selectionContext.accountTypeOptions.length > 0)
      ? selectionContext.accountTypeOptions
      : accountTypes;
    const narrowed = filterAccountTypesForCategory(category, base);
    return narrowed.map((option) => ({ value: option.code, label: option.label, record: option }));
  }, [selectionContext.accountTypeOptions, accountTypes, category]);

  // Determine whether lender supports payment offer (prefer child-level flag; fallback to parent)
  const lenderSupportsPaymentOffer = useMemo(() => {
    const childFlag = selectionContext.selectedChildRaw?.customerPaymentOffer === 'Y';
    const parentBool = selectionContext.parentRecord?.customerPaymentOffer === true;
    const parentRawFlag = selectionContext.parentRecord?.raw?.customerPaymentOffer === 'Y';
    return Boolean(childFlag || parentBool || parentRawFlag);
  }, [selectionContext.selectedChildRaw, selectionContext.parentRecord]);

  const existingOfferPresent = useMemo(() => {
    const amt = draft.paymentOfferAmount ?? 0;
    return Boolean((typeof amt === 'number' && amt > 0) || draft.paymentOfferId);
  }, [draft.paymentOfferAmount, draft.paymentOfferId]);

  const showPaymentOfferPanel = useMemo(() => {
    // If what's left over is <= 0, only show payment offer if one already exists
    // (so user can reduce/clear it). Otherwise it's hidden.
    if (typeof availableBudget !== 'number' || Number.isNaN(availableBudget)) {
      return false;
    }
    if (availableBudget <= 0) {
      return existingOfferPresent;
    }
    return lenderSupportsPaymentOffer || existingOfferPresent;
  }, [lenderSupportsPaymentOffer, availableBudget, existingOfferPresent]);

  const referenceHelpCompanyName = useMemo(() => {
    if (manualCompanyName.trim()) {
      return manualCompanyName;
    }
    if (productSelection.trim()) {
      return productSelection;
    }
    if (companySelection.trim()) {
      return companySelection;
    }
    if (draft.creditorName?.trim()) {
      return draft.creditorName;
    }
    return undefined;
  }, [companySelection, draft.creditorName, manualCompanyName, productSelection]);

  const {
    helpDialog: debtReferenceHelpDialog,
    helpLinkText: debtReferenceHelpLinkText,
    noValueCheckboxLabel: debtNoValueCheckboxLabel,
    label: debtReferenceLabel,
  } = useReferenceIdHelp({
    companyName: referenceHelpCompanyName,
    backendName: selectionContext.effectiveLenderName ?? draft.lenderName ?? undefined,
  });

  // Debug: whenever the draft payment-offer changes, log it so we can see what the dialog sees.
  React.useEffect(() => {
    if (draft.paymentOfferAmount != null || draft.paymentOfferId != null) {
      // eslint-disable-next-line no-console
      console.log('[Debts] DebtCategory draft payment offer', {
        category,
        creditorName: draft.creditorName,
        accountNumber: draft.accountNumber,
        paymentOfferAmount: draft.paymentOfferAmount,
        paymentOfferMode: draft.paymentOfferMode,
        paymentOfferId: draft.paymentOfferId,
        showPaymentOfferPanel,
      });
    }
  }, [
    category,
    draft.accountNumber,
    draft.creditorName,
    draft.paymentOfferAmount,
    draft.paymentOfferMode,
    draft.paymentOfferId,
    showPaymentOfferPanel,
  ]);

  const repaymentMonthlyForBudget = useMemo(() => {
    return toMonthly(draft.repaymentAmount ?? null, draft.repaymentFrequency ?? null) ?? 0;
  }, [draft.repaymentAmount, draft.repaymentFrequency]);

  const originalRepaymentMonthlyForBudget = useMemo(() => {
    const original = editingOriginalRef.current;
    const serverId = coerceNumericId(original?.id);
    if (!isEditing || !serverId) {
      return 0;
    }
    return toMonthly(original?.repaymentAmount ?? null, original?.repaymentFrequency ?? null) ?? 0;
  }, [isEditing]);

  const offerAmountForBudget = useMemo(() => {
    // Use the raw input string so decimals are reflected live even during typing.
    const raw = (paymentOfferAmountInput ?? '').trim();
    if (!raw || raw === '.') return 0;
    const parsed = parseFloat(raw.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }, [paymentOfferAmountInput]);

  const originalOfferAmountForBudget = useMemo(() => {
    const original = editingOriginalRef.current;
    const serverId = coerceNumericId(original?.id);
    if (!isEditing || !serverId) {
      return 0;
    }
    return originalOfferAmountBaseline;
  }, [isEditing, originalOfferAmountBaseline]);

  // Remaining budget after current payment offer amount (client-side only when API budget is > 0)
  const effectiveAvailableBudget = useMemo(() => {
    if (typeof availableBudget !== 'number' || Number.isNaN(availableBudget)) {
      return null;
    }

    // IMPORTANT:
    // `availableBudget` (balanceAmount) already includes the *saved* repayment + saved payment offer.
    // When editing an existing server-backed debt, compute a baseline that adds those saved values back
    // before subtracting the current (possibly edited) values. This avoids double-subtracting.
    const isEditingExisting =
      isEditing && coerceNumericId(draft.id ?? editingOriginalRef.current?.id) !== null;
    const baseline =
      availableBudget +
      (isEditingExisting ? originalRepaymentMonthlyForBudget : 0) +
      (isEditingExisting ? originalOfferAmountForBudget : 0);

    // Live calc based purely on current field values:
    // baseline - repaymentMonthly - paymentOfferAmount
    return baseline - repaymentMonthlyForBudget - offerAmountForBudget;
  }, [
    availableBudget,
    draft.id,
    isEditing,
    offerAmountForBudget,
    originalOfferAmountForBudget,
    originalRepaymentMonthlyForBudget,
    repaymentMonthlyForBudget,
  ]);

  const paymentOfferExceedsBudget = useMemo(() => {
    // Use live offer amount so error appears while typing (decimals supported).
    if (offerAmountForBudget <= 0) {
      return false;
    }
    if (typeof effectiveAvailableBudget === 'number') {
      return effectiveAvailableBudget < 0;
    }
    return false;
  }, [offerAmountForBudget, effectiveAvailableBudget]);

  const paymentOfferErrorMessage =
    formErrors.paymentOfferAmount ??
    (paymentOfferExceedsBudget ? 'Offer exceeds amount available' : undefined);

  // Determine if current category has a locked account type (e.g., Credit Cards, Overdrafts)
  const lockedCategoryType = useMemo(() => LOCKED_CATEGORY_TYPES[category], [category]);

  useEffect(() => {
    try {
      const customerDataStr = localStorage.getItem('customerData');
      if (!customerDataStr) return;
      const customerData = JSON.parse(customerDataStr);
      if (customerData?.mobileNumber) {
        setFreepostNotes(
          `I am in financial difficulty and because of this I have used IE Hub to review my budget.  I have enclosed a copy for your record. You will be able to see from this information that I'm unable to make my full contractual payments to you. Please call me on ${customerData.mobileNumber} so we can discuss my options.`,
        );
      } else {
        setFreepostNotes(
          "I am in financial difficulty and because of this I have used IE Hub to review my budget.  I have enclosed a copy for your record. You will be able to see from this information that I'm unable to make my full contractual payments to you.",
        );
      }
    } catch {
      // ignore parse errors; keep notes empty
    }
  }, []);

  useEffect(() => {
    if (productSelection !== selectionContext.resolvedProductSelection) {
      setProductSelection(selectionContext.resolvedProductSelection);
    }
  }, [productSelection, selectionContext.resolvedProductSelection]);

  useEffect(() => {
    setDraft((prev) => {
      let next = prev;
      const computedParent = selectionContext.manual
        ? null
        : selectionContext.parentRecord?.parentCompany ??
        (companySelection && companySelection !== MANUAL_COMPANY_OPTION ? companySelection : null);

      if ((prev.parentCompanyName ?? null) !== (computedParent ?? null)) {
        next = next === prev ? { ...prev } : next;
        next.parentCompanyName = computedParent ?? null;
      }

      const computedLender = selectionContext.effectiveLenderName ?? null;
      if ((prev.lenderName ?? null) !== computedLender) {
        next = next === prev ? { ...next } : next;
        next.lenderName = computedLender;
      }

      const computedCreditor = selectionContext.effectiveCreditorName;
      if ((prev.creditorName ?? '') !== (computedCreditor ?? '')) {
        next = next === prev ? { ...next } : next;
        next.creditorName = computedCreditor;
      }

      const computedDigital = Boolean(selectionContext.parentRecord);
      if (prev.isDigital !== computedDigital) {
        next = next === prev ? { ...next } : next;
        next.isDigital = computedDigital;
      }

      const computedSchemaNameRaw =
        (selectionContext.selectedChildRaw as any)?.schemaName ??
        (selectionContext.parentRecord?.raw as any)?.schemaName ??
        null;
      const computedSchemaName =
        typeof computedSchemaNameRaw === 'string' && computedSchemaNameRaw.trim().length > 0
          ? computedSchemaNameRaw
          : null;
      if (prev.schemaName !== computedSchemaName) {
        next = next === prev ? { ...next } : next;
        next.schemaName = computedSchemaName;
      }

      const computedSubDomain =
        (selectionContext.selectedChildRaw as unknown as { subDomain?: string } | undefined)?.subDomain ??
        (selectionContext.parentRecord?.raw as unknown as { subDomain?: string } | undefined)?.subDomain ??
        null;
      if (prev.subDomain !== computedSubDomain) {
        next = next === prev ? { ...next } : next;
        next.subDomain = computedSubDomain;
      }

      return next === prev ? prev : next;
    });
  }, [selectionContext, companySelection]);

  useEffect(() => {
    // Auto-apply locked account type when category requires it
    if (lockedCategoryType) {
      setDraft((prev) => {
        if (prev.accTypeCode === lockedCategoryType.code && prev.accountTypeName === lockedCategoryType.label) {
          return prev;
        }
        return {
          ...prev,
          accTypeCode: lockedCategoryType.code,
          accTypeId: prev.accTypeId ?? null,
          accountTypeName: lockedCategoryType.label,
        };
      });
      return; // skip dynamic account type auto-selection for locked categories
    }

    const singleFiltered = filteredAccountTypeOptions.length === 1 ? filteredAccountTypeOptions[0]?.value : null;
    const autoCode = singleFiltered || selectionContext.autoSelectAccountType;
    if (autoCode && draft.accTypeCode !== autoCode) {
      const record =
        selectionContext.accountTypeOptions.find((item) => item.code === autoCode) ??
        accountTypes.find((item) => item.code === autoCode);
      setDraft((prev) => {
        if (prev.accTypeCode === autoCode) {
          return prev;
        }
        return {
          ...prev,
          accTypeCode: autoCode,
          accTypeId: record?.id ?? null,
          accountTypeName: record?.label ?? null,
          debtType: record?.debtType ?? prev.debtType ?? null,
        };
      });
    } else if (
      category !== 'arrears' &&
      !selectionContext.accountTypeRequired &&
      draft.accTypeCode &&
      // Do not auto-clear user selection when account type is allowed before company selection
      !forceShowAccountType
    ) {
      setDraft((prev) => ({
        ...prev,
        accTypeCode: null,
        accTypeId: null,
        accountTypeName: null,
      }));
    }
  }, [
    selectionContext.accountTypeRequired,
    selectionContext.autoSelectAccountType,
    selectionContext.accountTypeOptions,
    accountTypes,
    filteredAccountTypeOptions,
    draft.accTypeCode,
    category,
    forceShowAccountType,
  ]);

  const resetDialogState = useCallback(() => {
    setPendingFocusField(null);
    setDraft(createEmptyEntry(category));
    setFormErrors({});
    setCompanySelection(DEFAULT_COMPANY_OPTION);
    setManualCompanyName('');
    setProductSelection('');
    setNoReferenceId(false);
    setIsSaving(false);
    setIsEditing(false);
    editingOriginalRef.current = null;
    paymentOfferClearedRef.current = false;
    paymentOfferInputDirtyRef.current = false;
    setPaymentOfferAmountInput('');
    amountOwedInputDirtyRef.current = false;
    repaymentAmountInputDirtyRef.current = false;
    setAmountOwedInput('');
    setRepaymentAmountInput('');
    setOriginalOfferAmountBaseline(0);
  }, [category]);

  // Open the edit view at the field the "complete this debt" link points to
  useEffect(() => {
    if (!dialogOpen || !pendingFocusField) return undefined;
    const fieldPath = `debt-${category}-${pendingFocusField}`;
    const timer = window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('iehub:focus-field', { detail: { fieldPath } }));
      setPendingFocusField(null);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [dialogOpen, pendingFocusField, category]);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetDialogState();
  };

  const handleAddDebt = () => {
    if (loading) return;
    editingOriginalRef.current = null;
    setIsEditing(false);
    resetDialogState();
    setDialogOpen(true);
  };

  const handleEditDebt = (entry: DebtFormEntry, focusField?: DebtPromptField) => {
    if (loading) return;
    setPendingFocusField(focusField ?? null);
    setIsEditing(true);
    paymentOfferClearedRef.current = false;
    paymentOfferInputDirtyRef.current = false;
    // Capture the original entry for comparison on save (detect referral unlink)
    editingOriginalRef.current = {
      ...entry,
    };
    // Debug: log the entry we are about to edit so we can verify payment-offer fields
    // eslint-disable-next-line no-console
    console.log('[Debts] handleEditDebt entry', {
      id: entry.id,
      category: entry.category,
      creditorName: entry.creditorName,
      accountNumber: entry.accountNumber,
      paymentOfferAmount: entry.paymentOfferAmount,
      paymentOfferMode: entry.paymentOfferMode,
      paymentOfferId: entry.paymentOfferId,
    });
    const existingAccountNumberRaw = entry.accountNumber ?? '';
    const userEmail =
      typeof user?.emailAddress === 'string' ? user.emailAddress.trim() : '';
    const isEmailFallback =
      userEmail &&
      existingAccountNumberRaw &&
      existingAccountNumberRaw.trim().toLowerCase() ===
      userEmail.toLowerCase();
    const accountNumberForDraft = isEmailFallback ? '' : existingAccountNumberRaw;
    let baseDraft: DraftDebtEntry = {
      ...entry,
      tempId: entry.tempId ?? generateTempId(),
      accountNumber: accountNumberForDraft,
    };

    // Defensive re-hydration from raw API record for payment offers.
    // In some flows the mapped `paymentOfferAmount`/`paymentOfferMode` on the entry
    // may be lost or stale, but `entry.raw` (CustomerDebtRecord) still has the
    // latest values from `/ie-customer-payment-offer`.
    const raw = (entry as unknown as { raw?: CustomerDebtRecord | undefined })?.raw;
    if (raw) {
      const rawAmount = (raw as any).paymentOfferAmount as number | undefined;
      if (
        (baseDraft.paymentOfferAmount == null || Number.isNaN(baseDraft.paymentOfferAmount)) &&
        typeof rawAmount === 'number' &&
        !Number.isNaN(rawAmount) &&
        rawAmount > 0
      ) {
        baseDraft = {
          ...baseDraft,
          paymentOfferAmount: rawAmount,
        };
      }
      if (!baseDraft.paymentOfferMode && (raw as any).paymentOfferMode) {
        baseDraft = {
          ...baseDraft,
          paymentOfferMode: (raw as any).paymentOfferMode as any,
        };
      }
      if (baseDraft.paymentOfferId == null && (raw as any).paymentOfferId != null) {
        baseDraft = {
          ...baseDraft,
          paymentOfferId: (raw as any).paymentOfferId as number,
        };
      }
    }

    // Baseline offer amount at dialog open (used to avoid double-subtracting against balanceAmount).
    setOriginalOfferAmountBaseline(
      typeof baseDraft.paymentOfferAmount === 'number' && !Number.isNaN(baseDraft.paymentOfferAmount)
        ? baseDraft.paymentOfferAmount
        : 0,
    );

    let derivedCompany = DEFAULT_COMPANY_OPTION;
    let derivedProduct = '';
    let derivedManualName = '';

    const parentName = entry.parentCompanyName ?? null;
    if (parentName) {
      const parentRecord = lenderLookup.byParent.get(normalize(parentName));
      if (parentRecord) {
        derivedCompany = parentRecord.parentCompany;
        const candidateProduct = entry.lenderName ?? entry.creditorName ?? '';
        if (candidateProduct) {
          const childMatch = lenderLookup.childToParent.get(normalize(candidateProduct));
          if (childMatch && normalize(childMatch.parent.parentCompany) === normalize(parentRecord.parentCompany)) {
            derivedProduct = childMatch.childRaw?.lender ?? candidateProduct;
          }
        }
      }
    }

    if (derivedCompany === DEFAULT_COMPANY_OPTION) {
      const candidate = entry.creditorName ?? entry.lenderName ?? '';
      if (candidate) {
        const childMatch = lenderLookup.childToParent.get(normalize(candidate));
        if (childMatch) {
          derivedCompany = childMatch.parent.parentCompany;
          derivedProduct = childMatch.childRaw?.lender ?? candidate;
        } else {
          const parentMatch = lenderLookup.byParent.get(normalize(candidate));
          if (parentMatch) {
            derivedCompany = parentMatch.parentCompany;
          } else {
            derivedCompany = MANUAL_COMPANY_OPTION;
            derivedManualName = candidate;
          }
        }
      } else {
        derivedCompany = MANUAL_COMPANY_OPTION;
      }
    }

    const resolvedManualName =
      derivedCompany === MANUAL_COMPANY_OPTION ? (derivedManualName || entry.creditorName || '') : '';

    setDraft({
      ...baseDraft,
      parentCompanyName: derivedCompany === MANUAL_COMPANY_OPTION ? null : derivedCompany,
      lenderName: derivedProduct ? derivedProduct : null,
      creditorName:
        derivedCompany === MANUAL_COMPANY_OPTION
          ? resolvedManualName
          : derivedProduct || (derivedCompany && derivedCompany !== MANUAL_COMPANY_OPTION ? derivedCompany : entry.creditorName ?? ''),
    });
    setPaymentOfferAmountInput(formatUpToTwoDecimals(baseDraft.paymentOfferAmount));
    amountOwedInputDirtyRef.current = false;
    repaymentAmountInputDirtyRef.current = false;
    setAmountOwedInput(formatUpToTwoDecimals(baseDraft.amountOwed));
    setRepaymentAmountInput(formatUpToTwoDecimals(baseDraft.repaymentAmount));
    setCompanySelection(derivedCompany);
    setManualCompanyName(resolvedManualName);
    setProductSelection(derivedProduct);
    setNoReferenceId(
      !accountNumberForDraft || accountNumberForDraft.trim().length === 0,
    );
    setFormErrors({});
    setDialogOpen(true);

    // Final defensive step: fetch the latest payment-offer from API on edit-open
    // to guarantee the dialog sees the most recent amount/day even if any prior
    // mapping/merging step lost it.
    (async () => {
      try {
        const customerId = await getCustomerId();
        const numericDebtId =
          typeof entry.id === 'number'
            ? entry.id
            : typeof entry.id === 'string'
              ? Number(entry.id)
              : null;
        const accNum = (entry.accountNumber ?? '').trim();
        if (!customerId || !entry.creditorName || !accNum) return;

        const resp = await DebtsService.getPaymentOffer(
          Number(customerId),
          entry.creditorName,
          accNum,
          { skipErrorCapture: true },
        );
        const payload = (resp?.data as any) ?? null;
        const latest =
          Array.isArray(payload) && payload.length > 0
            ? (payload[payload.length - 1] as any)
            : payload;
        if (latest) {
          setDraft((prev) => ({
            ...prev,
            paymentOfferAmount:
              typeof latest.paymentOfferAmount === 'number' && !Number.isNaN(latest.paymentOfferAmount)
                ? latest.paymentOfferAmount
                : prev.paymentOfferAmount ?? null,
            paymentOfferMode:
              // UI wants the preferred payment day, not the backend constant "M".
              latest.paymentPreferredFrequency ??
              latest.paymentOfferMode ??
              prev.paymentOfferMode ??
              null,
            paymentOfferId:
              typeof latest.id === 'number' && latest.id > 0
                ? latest.id
                : prev.paymentOfferId ?? null,
          }));
          // Keep the "original" baseline in sync with latest API offer so balanceAmount adjustments are correct.
          if (editingOriginalRef.current) {
            editingOriginalRef.current = {
              ...editingOriginalRef.current,
              paymentOfferAmount:
                typeof latest.paymentOfferAmount === 'number' && !Number.isNaN(latest.paymentOfferAmount)
                  ? latest.paymentOfferAmount
                  : (editingOriginalRef.current.paymentOfferAmount ?? null),
              paymentOfferId:
                typeof latest.id === 'number' && latest.id > 0
                  ? latest.id
                  : (editingOriginalRef.current.paymentOfferId ?? null),
            };
          }

          // Update baseline offer amount from latest API record (this triggers recalculation).
          setOriginalOfferAmountBaseline(
            typeof latest.paymentOfferAmount === 'number' && !Number.isNaN(latest.paymentOfferAmount)
              ? latest.paymentOfferAmount
              : 0,
          );
          // If the API fetch updated the offer and the user hasn't started typing, sync the input text too.
          if (!paymentOfferInputDirtyRef.current) {
            const nextText =
              typeof latest.paymentOfferAmount === 'number' && !Number.isNaN(latest.paymentOfferAmount)
                ? formatUpToTwoDecimals(latest.paymentOfferAmount)
                : '';
            setPaymentOfferAmountInput(nextText);
          }
        }
      } catch {
        // ignore - dialog will continue with whatever we already have
      }
    })();
  };

  const handleDeleteDebt = (entry: DebtFormEntry) => {
    if (readOnly || deletingEntryId !== null) return;
    const entryId = entry.id ?? entry.tempId;
    if (!entryId) return; // Safety check - should not happen
    setEntryToDelete(entry);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteDebt = async () => {
    if (!entryToDelete || readOnly || deletingEntryId !== null) return;
    const entryId = entryToDelete.id ?? entryToDelete.tempId;
    if (!entryId) return; // Safety check - should not happen

    setDeleteConfirmOpen(false);
    setDeletingEntryId(entryId);

    const performDelete = async () => {
      try {
        const customerId = await getCustomerId();
        const numericId = typeof entryToDelete.id === 'number' ? entryToDelete.id : (typeof entryToDelete.id === 'string' ? Number(entryToDelete.id) : null);
        if (!customerId || !numericId || Number.isNaN(numericId) || numericId <= 0) {
          // No server id - just remove locally
          const nextLocal = value.filter((item) => (item.id ?? item.tempId) !== entryId);
          onChange(nextLocal);
          // Refresh budget/totals (e.g., "what's left over") immediately
          try {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('iehub-debts-changed', {
                  detail: { action: 'deleted', debtId: entryToDelete.id ?? entryToDelete.tempId },
                }),
              );
            }
          } catch {
            // ignore event dispatch failures
          }
          setDeletingEntryId(null);
          setEntryToDelete(null);
          // Show success message
          try {
            window.dispatchEvent(
              new CustomEvent('iehub-alert', {
                detail: {
                  severity: 'success',
                  message: 'Debt deleted successfully',
                  variant: 'filled',
                  duration: 3000,
                },
              })
            );
          } catch {
            // no-op
          }
          return;
        }

        // IMPORTANT (legacy parity):
        // Before deleting a debt, ensure any pending payment offer is reset to 0 so
        // pending totals (pendingPaymentOffer) are cleared server-side.
        try {
          const accNum = (entryToDelete.accountNumber ?? '').trim();
          const directOfferId =
            typeof (entryToDelete as any).paymentOfferId === 'number' &&
            (entryToDelete as any).paymentOfferId > 0
              ? ((entryToDelete as any).paymentOfferId as number)
              : null;

          let offerId: number | null = directOfferId;
          if (!offerId && accNum) {
            const offerResp = await DebtsService.getPaymentOffer(
              Number(customerId),
              entryToDelete.creditorName,
              accNum,
              { skipErrorCapture: true },
            );
            const payload = (offerResp?.data as any) ?? null;
            const latest =
              Array.isArray(payload) && payload.length > 0
                ? (payload[payload.length - 1] as any)
                : payload;
            offerId = typeof latest?.id === 'number' && latest.id > 0 ? latest.id : null;
          }

          if (offerId) {
            await DebtsService.deletePaymentOffer(offerId, { skipErrorCapture: true });
          }
        } catch {
          // best-effort: do not block delete if offer reset fails
        }

        // If deleting a debt, check if there are linked share entries and update their source to "unlinked-debt"
        try {
          const sharingEntriesRaw = await SharePageService.getCustomerSharing(Number(customerId));
          const sharingEntries: CustomerSharingResponse[] = Array.isArray(sharingEntriesRaw)
            ? sharingEntriesRaw
            : [];

          const linkedEntries = sharingEntries.filter((entry) => entry.debtId === numericId);

          for (const entry of linkedEntries) {
            const sanitizedPayload = { ...entry, source: "unlinked-debt" };
            delete (sanitizedPayload as any).editable;

            await httpPut(sharePageEndpoints.createCustomerSharing(), sanitizedPayload);
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error("Failed to update share entries source:", error);
          // best-effort: do not block delete
        }

        await DebtsService.deleteDebt(Number(customerId), numericId, { skipErrorCapture: true });
        const next = value.filter((item) => (item.id ?? item.tempId) !== entryId);
        onChange(next);
        // Refresh budget/totals (e.g., "what's left over") immediately
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('iehub-debts-changed', {
                detail: { action: 'deleted', debtId: numericId },
              }),
            );
          }
        } catch {
          // ignore event dispatch failures
        }
        // Show success message
        try {
          window.dispatchEvent(
            new CustomEvent('iehub-alert', {
              detail: {
                severity: 'success',
                message: 'Debt deleted successfully',
                variant: 'filled',
                duration: 3000,
              },
            })
          );
        } catch {
          // no-op
        }
      } catch {
        // Fallback to local remove even if API errors (user intention is delete)
        const next = value.filter((item) => (item.id ?? item.tempId) !== entryId);
        onChange(next);
        // Refresh budget/totals (e.g., "what's left over") even on API error (best-effort)
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('iehub-debts-changed', {
                detail: { action: 'deleted', debtId: entryToDelete.id ?? entryToDelete.tempId },
              }),
            );
          }
        } catch {
          // ignore event dispatch failures
        }
        // Show success message even on API error (user intention is delete)
        try {
          window.dispatchEvent(
            new CustomEvent('iehub-alert', {
              detail: {
                severity: 'success',
                message: 'Debt deleted successfully',
                variant: 'filled',
                duration: 3000,
              },
            })
          );
        } catch {
          // no-op
        }
      } finally {
        setDeletingEntryId(null);
        setEntryToDelete(null);
      }
    };
    void performDelete();
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setEntryToDelete(null);
  };

  const handleCompanyDuplicateConfirmAdd = () => {
    if (!pendingCompanySelection) {
      setCompanyDuplicateDialogOpen(false);
      return;
    }
    const selected = pendingCompanySelection;
    setCompanyDuplicateDialogOpen(false);
    setPendingCompanySelection(null);
    // Apply the selection without re-running the duplicate guard.
    handleParentChange(selected, { fromDuplicateConfirm: true });
  };

  const handleCompanyDuplicateCancel = () => {
    // Simply close the dialog and clear the pending value.
    // We never applied the selection yet, so the visible value stays as before.
    setCompanyDuplicateDialogOpen(false);
    setPendingCompanySelection(null);
  };

  const handleParentChange = (parentValue: string, options?: { fromDuplicateConfirm?: boolean }) => {
    const fromDuplicateConfirm = options?.fromDuplicateConfirm ?? false;

    // Soft guard: if this company already exists in ANY debt section and we are adding
    // a new debt (not editing), show a confirmation dialog before applying the selection.
    if (
      !fromDuplicateConfirm &&
      !isEditing &&
      parentValue !== MANUAL_COMPANY_OPTION &&
      parentValue !== DEFAULT_COMPANY_OPTION
    ) {
      const normalizedSelected = normalize(parentValue);
      if (normalizedSelected && existingCreditorNameSet.has(normalizedSelected)) {
        setPendingCompanySelection(parentValue);
        setCompanyDuplicateDialogOpen(true);
        return;
      }
    }

    setCompanySelection(parentValue);
    setProductSelection('');
    setFormErrors((prev) => {
      const {
        company: _company,
        manualCompany: _manual,
        product: _product,
        creditorName: _creditor,
        accTypeCode: _accType,
        accountNumber: _accNum,
        ...rest
      } = prev;
      return rest;
    });

    if (parentValue === MANUAL_COMPANY_OPTION) {
      setManualCompanyName('');
      setDraft((prev) => ({
        ...prev,
        parentCompanyName: null,
        lenderName: null,
        creditorName: '',
        isDigital: false,
        schemaName: null,
        subDomain: null,
      }));
      return;
    }

    if (parentValue === DEFAULT_COMPANY_OPTION) {
      setManualCompanyName('');
      setDraft((prev) => ({
        ...prev,
        parentCompanyName: null,
        lenderName: null,
        creditorName: '',
        isDigital: false,
        schemaName: null,
        subDomain: null,
      }));
      return;
    }

    const parentRecord = lenderLookup.byParent.get(normalize(parentValue));
    const parentSchemaNameRaw = (parentRecord?.raw as any)?.schemaName;
    setManualCompanyName('');
    setDraft((prev) => ({
      ...prev,
      parentCompanyName: parentRecord ? parentRecord.parentCompany : parentValue,
      lenderName: null,
      creditorName: parentRecord ? parentRecord.parentCompany : parentValue,
      isDigital: Boolean(parentRecord),
      schemaName:
        typeof parentSchemaNameRaw === 'string' && parentSchemaNameRaw.trim().length > 0
          ? parentSchemaNameRaw
          : null,
      subDomain: (parentRecord?.raw as unknown as { subDomain?: string } | undefined)?.subDomain ?? null,
    }));
  };

  // Manual company name is now handled via FreeSelect (see dialog content)

  const handleProductChange = (nextValue: string) => {
    const parentRecord =
      companySelection !== MANUAL_COMPANY_OPTION && companySelection !== DEFAULT_COMPANY_OPTION
        ? lenderLookup.byParent.get(normalize(companySelection))
        : undefined;
    setProductSelection(nextValue);
    setDraft((prev) => ({
      ...prev,
      lenderName: nextValue ? nextValue : null,
      creditorName:
        nextValue ||
        (companySelection === MANUAL_COMPANY_OPTION
          ? manualCompanyName
          : parentRecord?.parentCompany ?? prev.creditorName),
    }));
    // Clear product error when user selects a product
    setFormErrors((prev) => {
      const { product: _product, ...rest } = prev;
      return rest;
    });
  };

  const handleFieldChange = <K extends keyof DraftDebtEntry>(field: K, value: DraftDebtEntry[K]) => {
    setDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear field-specific errors when user updates the field
    if (field === 'repaymentFrequency') {
      setFormErrors((prev) => {
        const { repaymentFrequency: _freq, ...rest } = prev;
        return rest;
      });
    } else if (field === 'paymentOfferAmount') {
      setFormErrors((prev) => {
        const { paymentOfferAmount: _offer, ...rest } = prev;
        return rest;
      });
    }
  };

  const accountMaxLength = useMemo(() => {
    const isCreditCard = category === 'creditCards' || draft.accTypeCode === 'ZC';
    return isCreditCard ? 4 : 50;
  }, [category, draft.accTypeCode]);

  const handleAccountNumberChange = (value: string) => {
    // Disallow single quotes and enforce max length (legacy rules)
    const original = value ?? '';
    const isCreditCard = category === 'creditCards' || draft.accTypeCode === 'ZC';

    let sanitized = original.replace(/'/g, '');
    if (isCreditCard) {
      sanitized = sanitized.replace(/\D/g, '');
    }
    sanitized = sanitized.slice(0, accountMaxLength);
    setNoReferenceId(false);
    handleFieldChange('accountNumber', sanitized);
    // Clear accountNumber error when user types
    setFormErrors((prev) => {
      const { accountNumber: _accNum, ...rest } = prev;
      return rest;
    });
  };

  const validateEntry = (entry: DraftDebtEntry): DebtFormErrors => {
    const errors: DebtFormErrors = {};

    if (!companySelection || companySelection === DEFAULT_COMPANY_OPTION) {
      errors.company = 'Please select a company';
    }

    /**
     * Company validation
     *
     * There are three modes:
     * - Explicit manual mode: user selected "I can't find my company" (MANUAL_COMPANY_OPTION)
     * - Lookup-backed mode: lender comes from ie-mstr (selectionContext.parentRecord present)
     * - Fallback/manual-like mode: company comes from an external list (e.g. energy mstr) and
     *   is *not* in the lender lookup. In this case `selectionContext.manual` may be true, but
     *   we must NOT require `manualCompanyName` because that field is not shown in the UI.
     */
    const isExplicitManualCompany = companySelection === MANUAL_COMPANY_OPTION;

    if (selectionContext.manual && isExplicitManualCompany) {
      if (!manualCompanyName.trim()) {
        errors.manualCompany = 'Please enter the company name';
      }
      // When using manual company entry, rely on the "Other Company name" field
      // and avoid showing a duplicate error on the main company select.
    } else if (!selectionContext.parentRecord && !selectionContext.manual) {
      // Only flag "invalid company" when we genuinely expected a lender lookup record.
      // Fallback/manual-like flows (e.g. energy companies) are allowed without a parentRecord.
      errors.company = 'Please select a valid company';
    }

    if (selectionContext.productRequired && !selectionContext.resolvedProductSelection) {
      errors.product = 'Please select a product';
    }

    if (
      // Only enforce a separate creditorName error when not in manual mode.
      // In manual mode, the "Other Company name" field carries the required validation.
      !selectionContext.manual &&
      (!selectionContext.effectiveCreditorName || !selectionContext.effectiveCreditorName.trim())
    ) {
      errors.creditorName = 'Company name is required';
    }

    // Require account type wherever the debt type dropdown is shown
    const shouldRequireAccountType =
      !hideAccountType &&
      !lockedCategoryType &&
      category !== 'arrears' &&
      (forceShowAccountType || selectionContext.showAccountType);

    if (shouldRequireAccountType && !entry.accTypeCode) {
      errors.accTypeCode = 'Select an account type';
    }

    if (!hideAccountType && category === 'arrears' && !entry.accTypeCode) {
      errors.accTypeCode = 'Select the arrears type';
    }

    // Edge case: validate 4-digit limit for credit cards if not already caught by input handler
    const isCreditCard = category === 'creditCards' || entry.accTypeCode === 'ZC';
    const currentAccountNumber = (entry.accountNumber ?? '').trim();
    const hasNonDigits = /\D/.test(currentAccountNumber);

    if (isCreditCard && !noReferenceId && (currentAccountNumber.length !== 4 || hasNonDigits)) {
      try {
        window.dispatchEvent(
          new CustomEvent('iehub-alert', {
            detail: {
              severity: 'warning',
              message: UI_MESSAGES.CREDIT_CARD_4_DIGIT_ALERT,
              variant: 'filled',
              duration: 3000,
            },
          })
        );
      } catch {
        // no-op
      }
      // Return a non-empty errors object to block the caller (upsertEntry), but without a field name to avoid red highlighting
      return { ...errors, __blockSave: true };
    }

    if (!entry.repaymentFrequency) {
      errors.repaymentFrequency = 'Select payment frequency';
    }

    // Payment offer amount can't exceed available budget (only when API budget was > 0)
    const paymentOfferAmount =
      typeof entry.paymentOfferAmount === 'number' && !Number.isNaN(entry.paymentOfferAmount)
        ? entry.paymentOfferAmount
        : null;
    if (
      typeof availableBudget === 'number' &&
      !Number.isNaN(availableBudget) &&
      paymentOfferAmount !== null &&
      paymentOfferAmount > 0
    ) {
      // Align with the UI "Amount available in your budget" calculation:
      // balanceAmount already includes saved repayment + saved offer when editing an existing debt.
      const repaymentMonthly =
        toMonthly(entry.repaymentAmount ?? null, entry.repaymentFrequency ?? null) ?? 0;
      const original = editingOriginalRef.current;
      const isEditingExisting =
        isEditing && coerceNumericId(entry.id ?? original?.id) !== null;
      const originalRepaymentMonthly =
        isEditingExisting
          ? (toMonthly(original?.repaymentAmount ?? null, original?.repaymentFrequency ?? null) ?? 0)
          : 0;
      const originalOffer =
        isEditingExisting
          ? (typeof original?.paymentOfferAmount === 'number' && !Number.isNaN(original.paymentOfferAmount)
            ? original.paymentOfferAmount
            : 0)
          : 0;
      const baseline =
        availableBudget +
        (isEditingExisting ? originalRepaymentMonthly : 0) +
        (isEditingExisting ? originalOffer : 0);
      const remaining = baseline - repaymentMonthly - paymentOfferAmount;

      if (typeof remaining === 'number' && remaining < 0) {
        errors.paymentOfferAmount = 'Offer exceeds amount available';
      }
    }

    return errors;
  };

  const upsertEntry = async () => {
    if (readOnly || isSaving) return;
    const errors = validateEntry(draft);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    // Prepare normalized values for duplicate detection before mutating UI state
    const rawAccountNumber = (draft.accountNumber ?? '').trim();
    const emailFallback =
      typeof user?.emailAddress === 'string' ? user.emailAddress.trim() : '';
    const accountNumberValue =
      (!noReferenceId && rawAccountNumber.length > 0
        ? rawAccountNumber
        : emailFallback) || '';
    const isLockedCategory = Boolean(lockedCategoryType);
    const effectiveAccTypeCode = isLockedCategory
      ? lockedCategoryType!.code
      : (draft.accTypeCode ?? null);
    const effectiveAccTypeRecord =
      effectiveAccTypeCode
        ? accountTypes.find((item) => item.code === effectiveAccTypeCode)
        : undefined;
    const effectiveCreditorName =
      selectionContext.effectiveCreditorName?.trim() || draft.creditorName || '';

    // Prefer checking against the full cross-section debts list when provided;
    // otherwise fall back to the current category's value only.
    const duplicateCollection: DebtFormEntry[] =
      Array.isArray(allExistingDebts) && allExistingDebts.length > 0 ? allExistingDebts : value;

    // Use the fallback account number (email) for duplicate detection when user
    // ticks "I don't have my reference id" so duplicates are still caught.
    const duplicateCandidate: DraftDebtEntry = {
      ...draft,
      accountNumber: accountNumberValue,
      creditorName: effectiveCreditorName,
      accTypeCode: effectiveAccTypeCode,
    };

    if (hasDuplicate(duplicateCandidate, duplicateCollection)) {
      setFormErrors({
        ...errors,
        accountNumber:
          'A debt already exists for this company with the same reference number.',
      });
      onDuplicateDetected?.(draft);
      setDuplicateCompany(draft.creditorName || '');
      setDuplicateDialogOpen(true);
      return;
    }

    setIsSaving(true);

    // A blank repayment stays blank (null), the same as amount owed. Never default it to 0.
    const repaymentAmountForApi = sanitizeNumber(draft.repaymentAmount);

    // Persist payment-offer amount from the input string so reopening the dialog
    // immediately after save has the value even before a refetch/enrichment.
    const paymentOfferAmountForSave = (() => {
      const existingDraftOffer =
        typeof draft.paymentOfferAmount === 'number' && !Number.isNaN(draft.paymentOfferAmount)
          ? draft.paymentOfferAmount
          : null;
      if (paymentOfferClearedRef.current) {
        return 0;
      }
      if (offerAmountForBudget > 0) {
        return offerAmountForBudget;
      }
      return existingDraftOffer;
    })();

    const sanitized: DebtFormEntry = {
      ...draft,
      parentCompanyName: selectionContext.manual
        ? null
        : selectionContext.parentRecord?.parentCompany ?? draft.parentCompanyName ?? null,
      lenderName: selectionContext.effectiveLenderName,
      creditorName: selectionContext.effectiveCreditorName.trim(),
      accountNumber: accountNumberValue,
      accTypeCode: effectiveAccTypeCode,
      accTypeId: effectiveAccTypeRecord?.id ?? (effectiveAccTypeCode ? draft.accTypeId ?? null : null),
      accountTypeName: isLockedCategory
        ? (lockedCategoryType!.label)
        : (effectiveAccTypeRecord?.label ?? (effectiveAccTypeCode ? draft.accountTypeName ?? null : null)),
      debtType:
        effectiveAccTypeRecord?.debtType ??
        draft.debtType ??
        (category === 'arrears' ? 'PRIORITY_DEBTS' : draft.debtType ?? null),
      amountOwed: sanitizeNumber(draft.amountOwed),
      repaymentAmount: repaymentAmountForApi,
      paymentOfferAmount: paymentOfferAmountForSave,
      shareNow: draft.shareNow ?? 'N',
      arrearsCode: draft.arrearsCode ?? (category === 'arrears' ? effectiveAccTypeCode ?? null : null),
      source:
        draft.source ??
        editingOriginalRef.current?.source ??
        (category === 'arrears' ? 'customer_expenses' : 'customer_debt'),
    };

    try {
      // Persist to API immediately (create or update)
      const customerId = await getCustomerId();
      if (!customerId) {
        // Can't persist; fall back to local update
        const identifier = sanitized.id ?? sanitized.tempId;
        const nextLocal = value.some((entry) => (entry.id ?? entry.tempId) === identifier)
          ? value.map((entry) => ((entry.id ?? entry.tempId) === identifier ? sanitized : entry))
          : [...value, sanitized];
        onChange(nextLocal);
        handleCloseDialog();
        return;
      }

      const normalizeForCompare = (value: string | null | undefined): string =>
        (value ?? '').trim().toLowerCase();
      const original = editingOriginalRef.current;
      const originalSourceRaw = original?.source as string | undefined;
      const baseSource = sanitized.source ?? originalSourceRaw ?? null;
      const originalSource = normalizeForCompare(originalSourceRaw);
      const isReferralDebt =
        originalSource === 'customer_profile' || originalSource === 'referred';
      const companyChanged =
        isReferralDebt &&
        normalizeForCompare(original?.creditorName) !== normalizeForCompare(sanitized.creditorName);
      const accTypeChanged =
        isReferralDebt &&
        normalizeForCompare(original?.accTypeCode ?? original?.accountTypeName) !==
        normalizeForCompare(sanitized.accTypeCode ?? sanitized.accountTypeName);

      const inferredSource =
        isReferralDebt && (companyChanged || accTypeChanged)
          ? 'unlink_customer_profile'
          : baseSource ??
          (category === 'arrears'
            ? 'customer_expenses'
            : category === 'creditCards'
              ? 'creditcard'
              : category === 'loans'
                ? 'loan'
                : category === 'overdrafts'
                  ? 'overdraft'
                  : category === 'otherDebts'
                    ? 'otherdebts'
                    : 'customer_debt');

      // Robustly coerce an existing debt id (including from the original/referral record)
      // so that edits to referred debts always use updateDebt (PUT) rather than createDebt (POST).
      const coerceNumericId = (value: unknown): number | null => {
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
          return value;
        }
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (!trimmed) {
            return null;
          }
          const parsed = Number(trimmed);
          if (Number.isFinite(parsed) && parsed > 0) {
            return parsed;
          }
        }
        return null;
      };

      const existingId = coerceNumericId(
        (sanitized as { id?: number | string | null })?.id ??
        (original as ({ id?: number | string | null } | null))?.id ??
        (original as ({ raw?: { id?: number | string | null } } | null))?.raw?.id,
      );

      const payload: DebtUpsertRequest = {
        ...(typeof existingId === 'number' && Number.isFinite(existingId) && existingId > 0
          ? { id: existingId }
          : {}),
        customerId: Number(customerId),
        creditor: sanitized.creditorName,
        accTypeCode: sanitized.accTypeCode ?? null,
        accountNumber: sanitized.accountNumber ?? null,
        amountOwed: sanitized.amountOwed ?? null,
        repayment: sanitized.repaymentAmount ?? null,
        frequency: mapDebtFrequencyUiToApi(sanitized.repaymentFrequency) ?? null,
        notes: sanitized.notes ?? null,
        shareBudget: sanitized.shareBudget ?? null,
        source: inferredSource,
        debtType: sanitized.debtType ?? (category === 'arrears' ? 'PRIORITY_DEBTS' : 'NON_PRIORITY_DEBTS'),
        repaymentMonthly: toMonthly(sanitized.repaymentAmount ?? null, sanitized.repaymentFrequency ?? null),
      };

      const isUpdate =
        typeof existingId === 'number' && Number.isFinite(existingId) && existingId > 0;
      const resp = isUpdate
        ? await DebtsService.updateDebt(payload, { skipErrorCapture: true })
        : await DebtsService.createDebt(payload, { skipErrorCapture: true });
      const saved = resp?.data as CustomerDebtRecord | undefined;
      debugLog('debt saved', {
        isUpdate,
        debtId: saved?.id ?? null,
        creditor: saved?.creditor ?? payload.creditor,
        accountNumber: saved?.accountNumber ?? payload.accountNumber,
        accTypeCode: saved?.accTypeCode ?? payload.accTypeCode,
      });

      // Payment offer upsert if present
      const offerAmount = sanitized.paymentOfferAmount ?? 0;
    if (saved && typeof saved.id === 'number' && offerAmount && offerAmount > 0) {
        const poPayload: PaymentOfferUpsertRequest = {
          customerId: Number(customerId),
          debtId: saved.id,
          lenderName: saved.lenderName || saved.creditor,
          accountNumber: saved.accountNumber ?? null,
          schemaName: (sanitized.schemaName ?? saved.schemaName) ?? null,
          paymentOfferAmount: offerAmount,
          // Legacy parity: payment. OfferMode is always "M" (monthly); the user's selection
          // (UI "Preferred payment day") maps to paymentPreferredFrequency.
          paymentOfferMode: 'M' as any,
          paymentPreferredFrequency: (sanitized.paymentOfferMode ?? null) as any,
        userId: typeof user?.userId === 'number' ? user.userId : null,
        lastUpdatedBy: typeof user?.userId === 'number' ? user.userId : null,
          ...(typeof sanitized.paymentOfferId === 'number' ? { id: sanitized.paymentOfferId } : {}),
        };
        try {
          if (typeof poPayload.id === 'number' && poPayload.id > 0) {
            await DebtsService.updatePaymentOffer(poPayload.id, poPayload, { skipErrorCapture: true });
          } else {
            const createdPo = await DebtsService.createPaymentOffer(poPayload, { skipErrorCapture: true });
            const createdPoData = createdPo?.data as any;
            if (createdPoData && typeof createdPoData.id === 'number') {
              sanitized.paymentOfferId = createdPoData.id;
            }
          }
        } catch {
          // Debug visibility: don't silently fail payment offer saves.
          // eslint-disable-next-line no-console
          console.warn('[Debts] Payment offer upsert failed', { poPayload });
        }
    } else if (
      saved &&
      typeof saved.id === 'number' &&
      paymentOfferClearedRef.current
    ) {
      // User cleared the offer: update to 0 so backend pendingPaymentOffer updates.
      const existingOfferId =
        (typeof sanitized.paymentOfferId === 'number' && sanitized.paymentOfferId > 0
          ? sanitized.paymentOfferId
          : typeof editingOriginalRef.current?.paymentOfferId === 'number'
            ? editingOriginalRef.current?.paymentOfferId
            : null);
      if (typeof existingOfferId === 'number' && existingOfferId > 0) {
        try {
          const poPayload: PaymentOfferUpsertRequest = {
            customerId: Number(customerId),
            debtId: saved.id,
            lenderName: saved.lenderName || saved.creditor,
            accountNumber: saved.accountNumber ?? null,
            schemaName: (sanitized.schemaName ?? saved.schemaName) ?? null,
            paymentOfferAmount: 0,
            paymentOfferMode: 'M' as any,
            paymentPreferredFrequency: null,
            userId: typeof user?.userId === 'number' ? user.userId : null,
            lastUpdatedBy: typeof user?.userId === 'number' ? user.userId : null,
            id: existingOfferId,
          };
          await DebtsService.updatePaymentOffer(existingOfferId, poPayload, { skipErrorCapture: true });
        } catch {
          // eslint-disable-next-line no-console
          console.warn('[Debts] Payment offer clear-to-0 failed', { existingOfferId });
        }
      }
      }

      // Notify listeners (e.g., Budget Breakdown / dashboard summary) to refresh debts + calculated totals.
      try {
        if (typeof window !== 'undefined') {
          window.dispatchEvent(
            new CustomEvent('iehub-debts-changed', { detail: { debtId: saved?.id } }),
          );
        }
      } catch {
        // ignore event dispatch failures
      }

      // Upsert sharing for this debt (digital/freepost)
      try {
        if (saved && typeof saved.id === 'number') {
          const lookups = await SharingOrchestrator.fetchLookups();
          const existing = await SharingOrchestrator.fetchExisting(Number(customerId));
          debugLog('calling upsertForDebt', {
            debtId: saved.id,
            creditor: saved.creditor,
            accountNumber: saved.accountNumber,
          });
          await SharingOrchestrator.upsertForDebt(Number(customerId), saved, lookups, existing, {
            freepostNotes,
            isManual: selectionContext.manual,
          });
          debugLog('upsertForDebt completed', { debtId: saved.id });
        } else {
          debugLog('skip upsertForDebt (missing saved id)', {
            savedId: saved?.id ?? null,
          });
        }
      } catch (err) {
        const anyErr = err as any;
        debugLog('upsertForDebt errored', {
          debtId: saved?.id ?? null,
          error: anyErr?.message ?? anyErr?.toString?.(),
          responseError: anyErr?.response?.data?.errorMessage ?? anyErr?.response?.data ?? null,
          status: anyErr?.response?.status ?? null,
        });
        // ignore sharing failure; not critical for debt save
      }

      // Update local form state with server-assigned id
      const identifier = sanitized.id ?? sanitized.tempId;
      const serverId = saved && typeof saved.id === 'number' ? saved.id : sanitized.id;
      const withId: DebtFormEntry = {
        ...sanitized,
        source: inferredSource,
        ...(serverId !== undefined ? { id: serverId } : {}),
      };
      const next = value.some((entry) => (entry.id ?? entry.tempId) === identifier)
        ? value.map((entry) => ((entry.id ?? entry.tempId) === identifier ? withId : entry))
        : [...value, withId];
      onChange(next);
      handleCloseDialog();
    } catch {
      // On API error, still update locally to avoid data loss
      const identifier = sanitized.id ?? sanitized.tempId;
      const next = value.some((entry) => (entry.id ?? entry.tempId) === identifier)
        ? value.map((entry) => ((entry.id ?? entry.tempId) === identifier ? sanitized : entry))
        : [...value, sanitized];
      onChange(next);
      handleCloseDialog();
    } finally {
      setIsSaving(false);
      paymentOfferClearedRef.current = false;
    }
  };

  const handleAccountTypeChange = (code: string) => {
    const trimmedCode = code?.trim();
    const record =
      accountTypes.find((item) => item.code === trimmedCode) ??
      selectionContext.accountTypeOptions.find((item) => item.code === trimmedCode);
    setDraft((prev) => ({
      ...prev,
      accTypeCode: trimmedCode && trimmedCode.length > 0 ? trimmedCode : null,
      accTypeId: record?.id ?? null,
      accountTypeName: record?.label ?? null,
      debtType: record?.debtType ?? prev.debtType ?? null,
    }));
    // Clear account type error when user selects an account type
    setFormErrors((prev) => {
      const { accTypeCode: _accType, ...rest } = prev;
      return rest;
    });
  };

  const currentEntries = value ?? [];
  const hasEntries = currentEntries.length > 0;

  if (loading) {
    return (
      <Box data-testid="components-debt-category-container-action-1">
        <Paper data-testid="components-debt-category-container-action-2"
          elevation={0}
          sx={{
            backgroundColor: 'white',
            borderRadius: 3,
            border: '1px solid #e6e6e6',
            overflow: 'hidden',
            p: 2,
          }}
        >
          <Box data-testid="components-debt-category-container-action-3"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography data-testid="components-debt-category-text-action-1" variant="h6" sx={{ fontSize: '20px', fontWeight: 400 }}>
              {label}
            </Typography>
            <Skeleton variant="rectangular" width={100} height={28} sx={{ borderRadius: 2 }} />
          </Box>
          <Stack data-testid="components-debt-category-container-action-4" spacing={1.5}>
            {[1, 2].map((i) => (
              <Skeleton key={i} variant="rectangular" height={72} sx={{ borderRadius: 2 }} />
            ))}
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box data-testid="components-debt-category-container-action-5">
      <Paper data-testid="components-debt-category-container-action-6"
        elevation={0}
        sx={{
          backgroundColor: 'white',
          borderRadius: 3,
          border: '1px solid #e6e6e6',
          overflow: 'hidden',
          p: 2,
        }}
      >
        {/* Header */}
        <Box data-testid="components-debt-category-container-action-7"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: hasEntries ? 'pointer' : 'default',
          }}
          onClick={() => hasEntries && setExpanded((prev) => !prev)}
        >
          <Stack data-testid="components-debt-category-container-action-8" direction="row" spacing={1.75} alignItems="center">
            <Typography data-testid="components-debt-category-text-action-2"
              variant="h6"
              sx={{
                fontWeight: 400,
                fontSize: { xs: '22px', md: '20px' },
                lineHeight: '24px',
              }}
            >
              {label}
            </Typography>
            {hasEntries && (
              <Chip data-testid="components-debt-category-chip-action"
                label={currentEntries.length}
                size="small"
                sx={{
                  fontWeight: 400,
                  fontSize: '18px',
                  backgroundColor: '#d4e50e',
                  color: '#003d40',
                  height: '24px',
                  borderRadius: '99px',
                  '& .MuiChip-label': {
                    px: 0.5,
                  }
                }}
              />
            )}
          </Stack>

          {/* Add button or collapse icon */}
          <Stack data-testid="components-debt-category-container-action-9" direction="row" spacing={1} alignItems="center">
            {!hasEntries && !readOnly && (
              <Button data-testid="components-debt-category-btn-add-debts"
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  event.stopPropagation();
                  handleAddDebt();
                }}
                disabled={loading}
                sx={{
                  backgroundColor: '#1a5053',
                  borderRadius: '3px',
                  px: 1,
                  py: 0.5,
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#155f54',
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: '4px',
                  }
                }}
              >
                Add debts
              </Button>
            )}
            {hasEntries && (
              <IconButton data-testid="components-debt-category-btn-action-1"
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  setExpanded((prev) => !prev);
                }}
                sx={{
                  p: 0,
                }}
              >
                {expanded ? <ExpandLessIcon sx={{ fontSize: 24 }} /> : <ExpandMoreIcon sx={{ fontSize: 24 }} />}
              </IconButton>
            )}
          </Stack>
        </Box>

        {/* Expandable content */}
        <Collapse in={expanded && hasEntries} unmountOnExit>
          <Box data-testid="components-debt-category-container-action-10" mt={2.75}>
            <Stack data-testid="components-debt-category-container-action-11" spacing={1.25}>
              {currentEntries.map((entry) => {
                const entryId = entry.id ?? entry.tempId;
                return (
                  <Paper data-testid="components-debt-category-container-action-12"
                    key={entryId}
                    elevation={0}
                    data-private
                    data-clarity-mask="true"
                    sx={{
                      borderRadius: 2,
                      backgroundColor: 'white',
                      border: '0px solid #e6e6e6',
                      p: 2,
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                    >
                    <Stack data-testid="components-debt-category-container-action-13" spacing={0.25}>
                      <Typography data-testid="components-debt-category-text-action-3"
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          fontSize: '16px',
                          lineHeight: '24px',
                          color: '#003d40',
                        }}
                      >
                        {lenderDisplayName(entry.creditorName) || 'Unknown company'}
                      </Typography>
                      <Typography data-testid="components-debt-category-text-ref:-1"
                        variant="body2"
                        sx={{
                          fontSize: '12px',
                          lineHeight: 'normal',
                          color: '#adb5bd',
                        }}
                      >
                        REF: {getReferenceDisplay(entry.accountNumber)}
                      </Typography>
                      <Typography data-testid="components-debt-category-text-amount-owed:-1"
                        variant="body2"
                        sx={{
                          fontSize: '14px',
                          lineHeight: '20px',
                          // Colours as in the debts list design: #939495 for the blank "-", #6B7A7B when there is an amount
                          color: isDebtAmountBlank(entry.amountOwed) ? '#939495' : '#6b7a7b',
                        }}
                      >
                        {isDebtAmountBlank(entry.amountOwed)
                          ? 'Amount owed: -'
                          : `Amount owed: ${formatCurrency(entry.amountOwed)}`}
                      </Typography>
                    </Stack>

                    <Stack data-testid="components-debt-category-container-action-14" direction="row" spacing={2} alignItems="center">
                      {!readOnly && (
                        <>
                          <IconButton data-testid="components-debt-category-btn-action-2"
                            size="small"
                            onClick={() => handleEditDebt(entry)}
                            disabled={deletingEntryId !== null}
                            sx={{ p: 0 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton data-testid="components-debt-category-btn-action-3"
                            size="small"
                            onClick={() => handleDeleteDebt(entry)}
                            disabled={deletingEntryId !== null}
                            sx={{
                              p: 0,
                              '&:disabled': {
                                opacity: 0.6,
                              },
                            }}
                          >
                            {deletingEntryId === (entry.id ?? entry.tempId) ? (
                              <CircularProgress size={16} sx={{ color: 'inherit' }} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </>
                      )}
                    </Stack>
                    </Box>
                    <DebtMissingPrompt
                      entry={entry}
                      disabled={readOnly || deletingEntryId !== null}
                      onAction={(field) => handleEditDebt(entry, field)}
                    />
                  </Paper>
                );
              })}
            </Stack>

            {/* Add another debt button */}
            {!readOnly && (
              <Button data-testid="components-debt-category-btn-handle-add-debt"
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddDebt}
                disabled={loading}
                sx={{
                  mt: 1.25,
                  border: '2px dashed #e1e5e5',
                  borderRadius: 3,
                  py: 1.875,
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#003d40',
                  textTransform: 'none',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 61, 64, 0.04)',
                    border: '2px dashed #e1e5e5',
                  },
                }}
              >
                Add another debt
              </Button>
            )}
          </Box>
        </Collapse>
      </Paper>

      <Dialog data-testid="components-debt-category-modal-action-1"
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle data-testid="components-debt-category-header-action-1"
          component="div"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 2,
          }}
        >
          <Typography data-testid="components-debt-category-text-action-4" variant="h6" sx={{ fontSize: '22px', fontWeight: 400, lineHeight: '24px' }}>
            {label}
          </Typography>
          <IconButton data-testid="components-debt-category-btn-handle-close-dialog-1"
            onClick={handleCloseDialog}
            size="small"
            sx={{
              backgroundColor: '#e0e0e0',
              borderRadius: '50%',
              p: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            px: 2,
            py: density === 'compact' ? 1.25 : 2,
            maxHeight: 'calc(90vh - 180px)',
            overflowY: 'auto',
          }}
        >
          <Stack data-testid="components-debt-category-container-action-15" spacing={density === 'compact' ? 1.0 : 2}>
            {/* Company Name (required) */}
            <Select data-testid="components-debt-category-select-company-name"
              label="Company name"
              dialogLabel="Select a Creditor"
              options={[
                { value: MANUAL_COMPANY_OPTION, label: 'I can\'t find my company' },
                ...companyOptions
                  .map((option) => ({
                    value: option,
                    label: lenderDisplayName(option) || option,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label)),
              ]}
              value={companySelection}
              onChange={(value) => handleParentChange(String(value))}
              error={Boolean(formErrors.company || formErrors.creditorName)}
              helperText={formErrors.company ?? formErrors.creditorName}
              validation={{ required: true }}
              deferValidation={true}
              disabled={readOnly}
              placeholder="Select a Company"
              helperTop="Select your creditor or choose 'I can't find my company' if not listed"
              showErrorBelow={false}
              searchable={true}
              fullWidth
              dataPrivate={false}
            />

            {companySelection === MANUAL_COMPANY_OPTION && (
              <FreeSelect data-testid="components-debt-category-select-other-company-name"
                label="Other Company name"
                options={[]}
                value={manualCompanyName}
                dataPrivate={false}
                onChange={(nextValue) => {
                  const stringValue =
                    typeof nextValue === 'string'
                      ? nextValue
                      : nextValue != null
                        ? String(nextValue)
                        : '';

                  // Always reflect what the user typed (prevents "last character removed" snapback).
                  setManualCompanyName(stringValue);

                  // If what the user typed exactly matches a real company option (case-insensitive),
                  // automatically switch the main company select to that company and behave as if
                  // the user selected it from the dropdown (incl. duplicate confirmation behavior).
                  const normalizedTyped = normalize(stringValue);
                  const matchedCompany =
                    normalizedTyped && Array.isArray(companyOptions)
                      ? companyOptions.find((name) => normalize(name) === normalizedTyped) ?? null
                      : null;

                  // Maintain manual-mode draft while the match is being resolved (or if it doesn't match).
                  setDraft((prev) => ({
                    ...prev,
                    parentCompanyName: null,
                    creditorName: stringValue,
                  }));
                  setFormErrors((prev) => {
                    const { manualCompany: _manual, creditorName: _creditor, ...rest } = prev;
                    return rest;
                  });

                  if (matchedCompany) {
                    handleParentChange(matchedCompany);
                  }
                }}
                error={Boolean(formErrors.manualCompany)}
                helperText={formErrors.manualCompany}
                disabled={readOnly}
                fullWidth
                validation={{ required: true }}
                deferValidation={true}
                helperTop="Start typing to search our Freepost list, or enter the company name manually"
                loadFreepostLenders={true}
              />
            )}

            {selectionContext.showProduct && (
              <Select data-testid="components-debt-category-select-product-name"
                label="Product name"
                dialogLabel={`Select a Product`}
                options={selectionContext.productOptions}
                value={productSelection}
                onChange={(value) => handleProductChange(String(value))}
                error={Boolean(formErrors.product)}
                helperText={formErrors.product}
                disabled={readOnly}
                placeholder="Select a product"
                showErrorBelow={true}
                fullWidth
                dataPrivate={false}
              />
            )}

            {!hideAccountType && category !== 'arrears' && (forceShowAccountType || selectionContext.showAccountType) && !lockedCategoryType && (
              <Select data-testid="components-debt-category-select-debt-type"
                label="Debt type"
                dialogLabel="Select an Account Type"
                options={filteredAccountTypeOptions.map(option => ({ value: option.value, label: option.label }))}
                value={draft.accTypeCode ?? ''}
                onChange={(value) => handleAccountTypeChange(String(value))}
                error={Boolean(formErrors.accTypeCode)}
                helperText={formErrors.accTypeCode}
                placeholder="Select an account type"
                disabled={readOnly}
                showErrorBelow={true}
                searchable={false}
                fullWidth
                validation={{ required: true }}
                deferValidation={true}
                dataPrivate={false}
              />
            )}

            {/* Reference Number (required unless customer explicitly doesn't have it) */}
            <SpecialTextField
              isAccountField={true}
              isCreditCardAccount={category === 'creditCards' || draft.accTypeCode === 'ZC'}
              digitsOnly={draft.accTypeCode === 'ZC'}
              label={debtReferenceLabel}
              placeholder={selectionContext.referencePlaceholder}
              value={draft.accountNumber ?? ''}
              onChange={(event) => handleAccountNumberChange(event.target.value)}
              validation={{
                maxLength: accountMaxLength,
              }}
              error={Boolean(formErrors.accountNumber)}
              helperText={formErrors.accountNumber}
              disabled={readOnly}
              fullWidth
              allowNoValue={true}
              noValueCheckboxLabel={debtNoValueCheckboxLabel}
              noValueChecked={noReferenceId}
              onNoValueChange={(checked) => {
                setNoReferenceId(checked);
                if (checked) {
                  setFormErrors((prev) => {
                    const { accountNumber: _accNum, ...rest } = prev;
                    return rest;
                  });
                }
              }}
              helpLinkText={debtReferenceHelpLinkText}
              helpDialog={debtReferenceHelpDialog}
              showErrorBelow={true}
            />

            {/* Debt Type - Read only for locked categories (Credit Cards, Overdrafts) */}
            {category !== 'arrears' && lockedCategoryType && (
              <Stack data-testid="components-debt-category-container-action-16" spacing={density === 'compact' ? 0.75 : 1.25} sx={{ mt: 0 }}>
                <Typography data-testid="components-debt-category-text-debt-type" variant="subtitle1" sx={{ fontWeight: 500, fontSize: '16px' }}>
                  Debt type<span style={{ color: 'red' }}>*</span>
                </Typography>
                <Box data-testid="components-debt-category-container-action-17"
                  sx={{
                    p: 0,
                    borderRadius: 3,
                    backgroundColor: 'white',
                  }}
                >
                  <Typography data-testid="components-debt-category-text-action-5" sx={{ fontSize: '16px', color: '#747474' }}>{lockedCategoryType.label}</Typography>
                </Box>
              </Stack>
            )}

            {/* Arrears Type - Required for arrears category */}
            {!hideAccountType && category === 'arrears' && (
              <Select data-testid="components-debt-category-select-arrears-type"
                label="Arrears Type"
                dialogLabel="Select an arrears type"
                options={accountTypes.map((rec) => ({ value: rec.code, label: rec.label }))}
                value={draft.accTypeCode ?? ''}
                onChange={(value) => handleAccountTypeChange(String(value))}
                error={Boolean(formErrors.accTypeCode)}
                helperText={formErrors.accTypeCode}
                placeholder="Select arrears type"
                disabled={readOnly || allAccountTypeOptions.length === 0}
                showErrorBelow={true}
                searchable={false}
                fullWidth
                validation={{ required: true }}
                deferValidation={true}
              />
            )}

            {/* Amount currently owed */}
            <Stack data-testid="components-debt-category-container-action-18" spacing={density === 'compact' ? 0.75 : 1.25}>
              <Typography data-testid="components-debt-category-text-amount-currently-owed" variant="subtitle1" sx={{ fontWeight: 500, fontSize: '16px' }}>
                Amount currently owed
              </Typography>
              <TextWithTooltip data-testid="components-debt-category-input--1"
                fieldPath={`debt-${category}-amountOwed`}
                value={amountOwedInput}
                onChange={(value) => {
                  // Extra safety: disallow negatives even if another input surface ever passes '-'.
                  const cleaned = value.replace(/,/g, '').replace(/-/g, '');
                  amountOwedInputDirtyRef.current = true;
                  setAmountOwedInput(cleaned);
                  if (cleaned.trim() === '' || cleaned === '.') {
                    handleFieldChange('amountOwed', null);
                    return;
                  }
                  const parsed = parseFloat(cleaned);
                  handleFieldChange('amountOwed', Number.isNaN(parsed) ? null : parsed);
                }}
                placeholder=""
                disabled={readOnly}
              />
            </Stack>

            {/* Repayment amount - Question Group */}
            <QuestionGroupForDialog
              label="Repayment amounts"
              flat
              labelFieldGap={1.5}
              showCalculatedIncome={true}
              amountValue={draft.repaymentAmount ?? null}
              frequencyValue={draft.repaymentFrequency ?? null}
            >
              {/* Amount field */}
              <TextWithTooltip data-testid="components-debt-category-input--2"
                fieldPath={`debt-${category}-repaymentAmount`}
                value={repaymentAmountInput}
                onChange={(value) => {
                  // Extra safety: disallow negatives even if another input surface ever passes '-'.
                  const cleaned = value.replace(/,/g, '').replace(/-/g, '');
                  repaymentAmountInputDirtyRef.current = true;
                  setRepaymentAmountInput(cleaned);
                  if (cleaned.trim() === '') {
                    handleFieldChange('repaymentAmount', null);
                    return;
                  }
                  // Intermediate typing value.
                  if (cleaned === '.') {
                    handleFieldChange('repaymentAmount', null);
                    return;
                  }
                  const parsed = parseFloat(cleaned);
                  handleFieldChange('repaymentAmount', Number.isNaN(parsed) ? null : parsed);
                }}
                placeholder=""
                disabled={readOnly}
              />

              {/* Frequency field */}
              <Box data-testid="components-debt-category-container-action-19" sx={{ mb: density === 'compact' ? 1 : 2 }}>
                <SelectToggle data-testid="components-debt-category-select-how-often?"
                  label="How often?"
                  options={REPAYMENT_OPTIONS.map(opt => ({
                    value: opt.value,
                    label: opt.label
                  }))}
                  value={draft.repaymentFrequency ?? ''}
                  onChange={(value) => handleFieldChange('repaymentFrequency', value as DebtRepaymentFrequencyUi)}
                  disabled={readOnly}
                />
                {formErrors.repaymentFrequency && (
                  <Typography data-testid="components-debt-category-text-action-6" sx={{ fontSize: '12px', color: 'error.main', mt: 0.5 }}>
                    {formErrors.repaymentFrequency}
                  </Typography>
                )}
              </Box>
            </QuestionGroupForDialog>

            {/* Payment Offer (conditional) */}
            {showPaymentOfferPanel && (
              <Stack data-testid="components-debt-category-container-action-20" spacing={density === 'compact' ? 0.75 : 1.25}
                sx={{ ...(paymentOfferExceedsBudget && { borderLeft: '4px solid', borderColor: 'error.main', pl: 2 }) }}
              >
                <Typography data-testid="components-debt-category-text-payment-offer" variant="subtitle1" sx={{ fontWeight: 500, fontSize: '16px' }}>
                  I would like to make a payment offer of
                </Typography>
                {/* Validation error shown directly under the heading (per Figma error state) */}
                {paymentOfferErrorMessage && (
                  <Typography data-testid="components-debt-category-text-action-7" sx={{ fontSize: '14px', fontWeight: 600, color: 'error.main' }}>
                    {paymentOfferErrorMessage}
                  </Typography>
                )}
                {/* Available budget shown ABOVE the amount field so the user sees it while entering the amount (per Figma) */}
                {typeof availableBudget === 'number' && (
                  <Box data-testid="components-debt-category-container-budget-info" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: '#003F44' }} />
                    <Typography data-testid="components-debt-category-text-amount-available-in-your-budget"
                      sx={{
                        fontSize: '12px',
                        color: '#6b7a7b',
                      }}
                    >
                      Amount available in your budget{' '}
                      {formatCurrency((effectiveAvailableBudget ?? availableBudget ?? 0)).replace('££', '£')}
                    </Typography>
                  </Box>
                )}
                {/* Stacked full-width on all breakpoints (per Figma — no side-by-side fields).
                    Previously md+ rendered Amount and Preferred payment day side by side. */}
                <Stack data-testid="components-debt-category-container-action-21" direction="column" spacing={2}>
                  <Box data-testid="components-debt-category-container-action-22" sx={{ flex: 1 }}>
                    <TextWithTooltip data-testid="components-debt-category-input--3"
                      value={paymentOfferAmountInput}
                      onChange={(value) => {
                        const cleaned = value.replace(/,/g, '');
                        if (cleaned.trim() === '') {
                          paymentOfferClearedRef.current = true;
                          paymentOfferInputDirtyRef.current = true;
                          setPaymentOfferAmountInput('');
                          handleFieldChange('paymentOfferAmount', null);
                          return;
                        }
                        paymentOfferClearedRef.current = false;
                        paymentOfferInputDirtyRef.current = true;
                        setPaymentOfferAmountInput(cleaned);
                        // Only skip numeric commit for a lone "." (user just started typing).
                        // Values like "0." or "12." should still count as 0/12 for live calculations.
                        if (cleaned === '.') {
                          return;
                        }
                        const parsed = parseFloat(cleaned);
                        handleFieldChange('paymentOfferAmount', isNaN(parsed) ? null : parsed);
                      }}
                      placeholder=""
                      disabled={readOnly}
                      suffix="/Monthly"
                    />
                  </Box>
                  <Box data-testid="components-debt-category-container-action-23" sx={{ flex: 1 }}>
                    <Select data-testid="components-debt-category-select-preferred-payment-day"
                      label="Preferred payment day"
                      dialogLabel="Preferred payment day"
                      options={[...Array.from({ length: 31 }).map((_, i) => ({ value: String(i + 1), label: String(i + 1) }))]}
                      value={draft.paymentOfferMode ? String(draft.paymentOfferMode) : ''}
                      onChange={(val) => handleFieldChange('paymentOfferMode', (val ? Number(val) : null) as any)}
                      disabled={readOnly}
                      placeholder="Please select"
                      showErrorBelow={false}
                      fullWidth
                    />
                  </Box>
                </Stack>
              </Stack>
            )}

            {/* Notes field — placed last to match the Figma question order */}
            <CollapsibleTextArea data-testid="components-debt-category-input-notes-(optional)"
              label="Notes (optional)"
              value={draft.notes ?? ''}
              onChange={(value) => handleFieldChange('notes', value)}
              onKeyDown={(e) => {
                if (
                  (draft.notes ?? "").length >= 200 &&
                  e.key.length === 1 &&
                  !e.ctrlKey &&
                  !e.metaKey
                ) {
                  showAlert({
                    severity: "warning",
                    message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                  });
                }
              }}
              onPaste={(e) => {
                const maxLength = 200;
                const pastedText = e.clipboardData.getData("text");
                const selectionStart = e.target.selectionStart || 0;
                const selectionEnd = e.target.selectionEnd || 0;
                const currentLength = (draft.notes ?? "").length;
                if (
                  currentLength - (selectionEnd - selectionStart) + pastedText.length >
                  maxLength
                ) {
                  showAlert({
                    severity: "warning",
                    message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                  });
                }
              }}
              maxLength={200}
              placeholder="Add notes..."
              disabled={readOnly}
            />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 2,
            gap: 1.5,
            justifyContent: 'flex-end',
          }}
        >
          <Button data-testid="components-debt-category-btn-handle-close-dialog-2"
            variant="outlined"
            onClick={handleCloseDialog}
            disabled={isSaving}
            sx={{
              flex: { xs: 1, md: 'initial' },
              minWidth: { xs: 'auto', md: '140px' },
              borderRadius: 2,
              py: 1.5,
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              backgroundColor: '#F2F2F2',
              borderColor: '#F2F2F2',
              color: '#1A1A1A',
              '&:hover': {
                backgroundColor: '#E8E8E8',
                borderColor: '#E8E8E8',
              },
              '&:disabled': {
                backgroundColor: '#F2F2F2',
                color: '#1A1A1A',
                opacity: 0.5,
              },
            }}
          >
            Cancel
          </Button>
          <Button data-testid="components-debt-category-btn-upsert-entry"
            variant="contained"
            onClick={upsertEntry}
            disabled={readOnly || loading || isSaving || paymentOfferExceedsBudget}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{
              flex: { xs: 1, md: 'initial' },
              minWidth: { xs: 'auto', md: '200px' },
              backgroundColor: '#004D44',
              borderRadius: 2,
              py: 1.5,
              fontSize: '16px',
              fontWeight: 600,
              textTransform: 'none',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#004D44',
                boxShadow: 'none',
              },
              '&:disabled': {
                backgroundColor: '#E8EFEE',
                color: '#00243E',
              },
            }}
          >
            {isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate detected dialog */}
      <Dialog data-testid="components-debt-category-modal-action-2"
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle data-testid="components-debt-category-header-duplicate-company-detected">Duplicate Company Detected</DialogTitle>
        <DialogContent dividers>
          <Typography data-testid="components-debt-category-text-action-8">
            {duplicateCompany
              ? `An entry for ${duplicateCompany} with this reference number already exists.`
              : 'An entry with this company and reference number already exists.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button data-testid="components-debt-category-btn-ok" variant="contained" onClick={() => setDuplicateDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* Company-level duplicate dialog (across all debt sections) */}
      <Dialog data-testid="components-debt-category-modal-action-3"
        open={companyDuplicateDialogOpen}
        onClose={handleCompanyDuplicateCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle data-testid="components-debt-category-header-debt-already-exists">Debt already exists</DialogTitle>
        <DialogContent dividers>
          <Typography data-testid="components-debt-category-text-action-9">
            {pendingCompanySelection
              ? `You already have at least one debt listed for ${pendingCompanySelection}.`
              : 'You already have a debt listed for this company.'}
          </Typography>
          <Typography data-testid="components-debt-category-text-action-10" sx={{ mt: 1.5 }}>
            Are you sure you want to add another debt for this company?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button data-testid="components-debt-category-btn-handle-company-duplicate-cancel" variant="outlined" onClick={handleCompanyDuplicateCancel}>
            Cancel
          </Button>
          <Button data-testid="components-debt-category-btn-handle-company-duplicate-confirm-add" variant="contained" onClick={handleCompanyDuplicateConfirmAdd}>
            Add another debt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog data-testid="components-debt-category-modal-action-4"
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle data-testid="components-debt-category-header-action-2"
          component="div"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 2,
          }}
        >
          <Typography data-testid="components-debt-category-text-delete-debt" variant="h6" sx={{ fontSize: '22px', fontWeight: 400, lineHeight: '24px' }}>
            Delete Debt
          </Typography>
          <IconButton data-testid="components-debt-category-btn-handle-cancel-delete-1"
            onClick={handleCancelDelete}
            size="small"
            sx={{
              backgroundColor: '#e0e0e0',
              borderRadius: '50%',
              p: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2, py: 2 }}>
          <Typography data-testid="components-debt-category-text-are-you-sure-you-want-to-delete-this-debt?" sx={{ fontSize: '16px', lineHeight: '24px' }}>
            Are you sure you want to delete this debt?
          </Typography>
          {entryToDelete && (
            <Box data-testid="components-debt-category-container-action-24" data-private data-clarity-mask="true" sx={{ mt: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
              <Typography data-testid="components-debt-category-text-action-11" sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }}>
                {lenderDisplayName(entryToDelete.creditorName) || 'Unknown company'}
              </Typography>
              <Typography data-testid="components-debt-category-text-ref:-2" sx={{ fontSize: '12px', color: '#6b7a7b' }}>
                REF: {getReferenceDisplay(entryToDelete.accountNumber)}
              </Typography>
              <Typography data-testid="components-debt-category-text-amount-owed:-2" sx={{ fontSize: '14px', color: '#6b7a7b', mt: 0.5 }}>
                Amount owed: {formatCurrency(entryToDelete.amountOwed)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 2,
            gap: 3,
            justifyContent: 'stretch',
          }}
        >
          <Button data-testid="components-debt-category-btn-handle-cancel-delete-2"
            fullWidth
            variant="outlined"
            onClick={handleCancelDelete}
            disabled={deletingEntryId !== null}
            sx={{
              borderColor: '#b7b9bc',
              borderRadius: 3,
              py: 1.5,
              fontSize: '18px',
              fontWeight: 400,
              textTransform: 'none',
              color: '#003d40',
              '&:hover': {
                borderColor: '#b7b9bc',
                backgroundColor: 'rgba(0, 61, 64, 0.04)',
              },
              '&:disabled': {
                borderColor: '#b7b9bc',
                opacity: 0.5,
              },
            }}
          >
            Cancel
          </Button>
          <Button data-testid="components-debt-category-btn-confirm-delete-debt"
            fullWidth
            variant="contained"
            onClick={confirmDeleteDebt}
            disabled={deletingEntryId !== null}
            startIcon={deletingEntryId !== null ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{
              backgroundColor: '#d32f2f',
              borderRadius: 3,
              py: 1.5,
              fontSize: '18px',
              fontWeight: 400,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#c62828',
              },
              '&:disabled': {
                backgroundColor: '#d32f2f',
                opacity: 0.6,
              },
            }}
          >
            {deletingEntryId !== null ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DebtCategory;


